// contexts/ThemeContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';

const ThemeContext = createContext();

// Define base themes first
const darkTheme = {
  name: 'dark',
  // Existing sidebar colors
  sidebarBg: '#2c2c2c',           
  sidebarText: '#ffffff',         
  sidebarHoverBg: '#3d3d3d',     
  sidebarSelectedBg: '#4a4a4a',   
  sidebarContextMenuBg: '#333333',
  sidebarContextMenuBorder: '#444444',
  sidebarScrollbarBg: '#444444',
  sidebarScrollbarHoverBg: '#555555',
  
  // Existing input and button colors
  inputBg: '#ffffff',
  inputBorder: '#444444',
  inputText: '#000000',
  buttonBg: '#333333',
  buttonHoverBg: '#444444',
  buttonText: '#ffffff',
  
  // Existing accent color
  accentColor: '#ffcc00',
  accentTextColor: '#ffffff',

  // Add MainPanel variables (matching dark theme)
  mainPanelBg: '#2c2c2c',           // Dark background
  mainPanelText: '#ffffff',         // White text
  mainPanelBorder: '#444444',       // Medium gray for borders
  mainPanelInputBg: '#333333',      // Slightly lighter for input backgrounds
  mainPanelInputBorder: '#444444',  // Medium gray for input borders
  mainPanelHeaderBg: '#3d3d3d',     // Slightly lighter for headers
  mainPanelAccent: '#ffcc00',       // Keep yellow accent
  
  // Button colors
  mainPanelButtonPrimary: '#333333',    // Dark gray base
  mainPanelButtonHover: '#444444',      // Lighter on hover
  mainPanelButtonText: '#ffffff',       // White text
  mainPanelButtonCancel: '#4a4a4a',     // Medium gray
  mainPanelButtonDanger: '#dc3545',     // Keep same red for delete

  // Modal colors
  mainPanelModalBg: '#2c2c2c',          // Dark background
  mainPanelModalOverlay: 'rgba(0, 0, 0, 0.5)',  // Dark overlay
  mainPanelModalBorder: '#444444',       // Medium gray border

  mainPanelStickysViewBg: '#2c2c2c',         // Container background
  mainPanelStickysViewText: '#ffffff',       // Header text color
  mainPanelStickysViewButtonBg: '#444444',   // Add button background
  mainPanelStickysViewButtonText: '#ffffff', // Add button text
  mainPanelStickysViewButtonHover: '#555555', // Add button hover

  // Right Panel specific
  rightPanelBg: '#2c2c2c',          // Dark background
  rightPanelText: '#ffffff',        // White text
  rightPanelBorder: '#444444',      // Medium gray border
  rightPanelShadow: 'rgba(0, 0, 0, 0.2)', // Dark shadow

  // Sticky Notes specific
  stickyNoteBg: '#333333',          // Slightly lighter dark
  stickyNoteText: '#ffffff',        // White text
  stickyNoteBorder: '#444444',      // Medium gray border
  stickyNoteDragBar: '#3d3d3d',     // Slightly lighter for drag bar
  stickyNoteButtonBg: '#2c2c2c',    // Darkest for buttons
  stickyNoteButtonText: '#ffffff',  // White text
  stickyNoteButtonHover: '#444444', // Medium gray for hover
  stickyNoteResizeHandle: '#444444', // Medium gray for handle
  

  // Modal specific (for sticky notes)
  stickyModalBg: '#2c2c2c',         // Dark background
  stickyModalText: '#ffffff',       // White text
  stickyModalOverlay: 'rgba(0, 0, 0, 0.5)', // Dark overlay
  stickyModalButtonPrimary: '#333333', // Dark gray for primary
  stickyModalButtonSecondary: '#4a4a4a' ,// Medium gray for secondary

  stickyNoteChecklistBg: '#333333',          // Dark background matching other stickies
  stickyNoteChecklistBorder: '#444444',      // Border matching theme
  stickyNoteChecklistLine: '#555555',        // Darker line for the input underline
  stickyNoteChecklistText: '#ffffff',        // White text
  stickyNoteChecklistTextChecked: '#888888', // Dimmed text for checked items
  stickyNoteChecklistAccent: '#666666' ,      // Accent color for checkbox
  stickyNoteChecklistBg: '#3d3d3d',          // Lighter than panel but darker than regular sticky
  stickyNoteChecklistDragBar: '#4a4a4a',     // Distinct from background
  stickyNoteChecklistButtonBg: '#4a4a4a',    // Button background
  stickyNoteChecklistButtonHover: '#555555',  // Button hover state
  stickyNoteChecklistButtonText: '#ffffff',   // Button text

   // List panel
 stickyListHeaderBg: '#3d3d3d',      // Light blue for list panel header 
 stickyListHeaderText: '#ffffff',    // Dark blue for header text

 codeBlockBg: '#686864', // Light gray for light theme
 codeBlockText: '#ffffff', // Black text for light theme
};

const lightTheme = {
  name: 'light',
// Clean modern colors (from the demo)
// 🎯 LEFT SIDEBAR CORE COLORS
panelBg: '#e4e9f2',         // Main background
panelText: '#1e293b',       // Main text  
panelBorder: '#e2e8f0',     // All borders
panelHover: '#f1f5f9',      // All hover states
panelSelected: '#dbeafe',     // Very light blue instead of bright blue
panelSelectedText: '#1e40af', // Dark blue text instead of white
panelDanger: '#1e293b',     // Logout button
panelMuted: '#64748b',      // Secondary text/icons
panelHeaderBg: '#cad3e6',     // Light blue-gray for category headers

countBadgeBg: '#e2e8f0',      // Note count background
badgeSuccess: '#10b981',      // "New" badge background
iconFolder: '#f59e0b',        // Orange folder icons
iconFile: '#3b82f6',          // Green file icons  
iconCode: '#3b82f6',          // Blue code icons
iconImage: '#8b5cf6',         // Purple image icons

//HOME PAGE HTML
//mainPanelBg: '#e4e9f2',           // Lightest blue for main background
//mainPanelBg: '#cad3e6',     // Slightly darker than current #e4e9f2
//mainPanelText: '#406ea7',         // Darkest blue for text
mainPanelBg: 'rgba(64, 110, 167, 0.5)', // Semi-transparent blue overlay     // White instead of light blue
mainPanelText: '#1e293b',      // Dark text instead of blue
codeBlockBg: '#fafafa',          // Light gray background
codeBlockText: '#000000',      // Black text
sidebarBg: '#406ea7',          // Darkest blue for sidebar background
buttonBg: '#406ea7',          // Darkest blue for button background
buttonHoverBg: '#5e81b3',     // Second darkest for button hover
buttonText: '#e4e9f2',        // Lightest blue for button text

mainPanelBorder: '#afbed9',       // Use your blue palette for borders
mainPanelInputBg: '#ffffff',     // White input backgrounds for contrast
mainPanelAccent: '#406ea7',      // Use your darkest blue for accents
mainPanelHeaderBg: '#cad3e6',    // Light blue headers (same as panelHeaderBg)
mainPanelButtonPrimary: '#406ea7', // Darkest blue for primary buttons
mainPanelButtonText: '#e4e9f2',  // Light blue button text
mainPanelButtonHover: '#5e81b3', // Second darkest for hover
mainPanelModalBg: '#ffffff',     // White modal background
mainPanelModalOverlay: 'rgba(64, 110, 167, 0.5)', // Semi-transparent blue overlay


bgToolbar: '#e2e8f0',        // Bright red - should be obvious if working
bgContent: '#e4e9f2',        // For --bg-content  
borderColor: '#afbed9',      // For --border-color (used in multiple places)
bgHover: '#f1f5f9',          // For --bg-hover
bgActive: '#cad3e6',         // For --bg-active
  



  // Right Panel specific
rightPanelBg: '#e4e9f2',          // Light blue background
rightPanelText: '#406ea7',        // Dark blue text
rightPanelBorder: '#5e81b3',      // Border color
rightPanelShadow: 'rgba(64, 110, 167, 0.1)', // Shadow with main blue

// Sticky Notes specific
stickyNoteBg: '#ffffff',          // White background
stickyNoteText: '#406ea7',        // Dark blue text
stickyNoteBorder: '#afbed9',      // Medium blue border
stickyNoteDragBar: '#cad3e6',     // Light blue for drag bar
stickyNoteButtonBg: '#cad3e6',     // Light blue-gray (matches panelHeaderBg)
stickyNoteButtonText: '#1e293b',   // Dark blue text (matches panelText)
stickyNoteButtonHover: '#afbed9',  // Medium blue for hover
stickyNoteResizeHandle: '#afbed9', // Medium blue for resize handle


// Modal specific (for sticky notes)
stickyModalBg: '#e4e9f2',         // Light blue background
stickyModalText: '#406ea7',       // Dark blue text
stickyModalOverlay: 'rgba(64, 110, 167, 0.5)', // Semi-transparent overlay
stickyModalButtonPrimary: '#406ea7', // Primary action button
stickyModalButtonSecondary: '#afbed9', // Secondary action button


// Checklist specific - for light theme
// Background and borders
// Checklist specific - should match regular sticky note colors
// Background and borders
stickyNoteChecklistBg: '#ffffff',          // White background - matches stickyNoteBg
stickyNoteChecklistBorder: '#afbed9',      // Medium blue border - matches stickyNoteBorder
stickyNoteChecklistDragBar: '#cad3e6',     // Light blue - matches stickyNoteDragBar

// Text and input styling
stickyNoteChecklistText: '#406ea7',        // Dark blue text - matches stickyNoteText
stickyNoteChecklistTextChecked: '#5e81b3', // Lighter blue for checked items
stickyNoteChecklistLine: '#afbed9',        // Medium blue underline - matches border
stickyNoteChecklistAccent: '#406ea7',      // Dark blue accent - matches theme

// Button styling
stickyNoteChecklistButtonBg: '#406ea7',    // Dark blue - matches stickyNoteButtonBg
stickyNoteChecklistButtonHover: '#5e81b3', // Hover state - matches stickyNoteButtonHover
stickyNoteChecklistButtonText: '#e4e9f2',  // Light text - matches stickyNoteButtonText

 // List panel
 stickyListHeaderBg: '#cad3e6',      // Light blue for list panel header 
 stickyListHeaderText: '#406ea7',    // Dark blue for header text


// Modern View Note List specific
modernViewCardBorder: '#afbed9',        // Border color for cards and elements
modernViewNoteItemBg: '#cad3e6',        // Note card background color
modernViewNoteItemHover: '#4170a5',     // Note card hover state
modernViewNoteItemSelected: '#5e81b3',  // Selected note card
modernViewNoteTitle: '#d1deed',         // Note title text color (with e4e9f2 for dark bg)
modernViewCardDropBg: '#dee5ef',         // dropdown background
modernViewDropdownText: '#406ea7',          // Main text color


// View Toggle specific
modernViewToggleBg: '#cad3e6',          // Toggle container background
modernViewToggleButtonBg: '#406ea7',    // Active toggle button background
modernViewToggleButtonActive: '#e4e9f2', // Active toggle button text
modernViewToggleButtonInactive: '#afbed9', // Inactive toggle button

// Modal specific for category filter
modernViewModalBg: '#ffffff',          // Modal background
modernViewModalOverlay: 'rgba(0, 0, 0, 0.5)', // Modal overlay
modernViewModalText: '#406ea7',        // Main text color matching your blue
modernViewModalHeaderBg: '#e4e9f2',    // Light blue header background
modernViewModalBorder: '#afbed9',      // Border color
modernViewModalToggleActive: '#406ea7', // Toggle switch when active
modernViewModalToggleInactive: '#cad3e6', // Toggle switch when inactive

  // Settings Modal specific
  settingsModalBg: '#ffffff',            // Modal background
  settingsModalText: '#406ea7',          // Main text color
  settingsModalBorder: '#afbed9',        // Border color
  settingsModalOverlay: 'rgba(64, 110, 167, 0.5)',  // Modal overlay using theme blue
  settingsModalHeaderBg: '#e4e9f2',      // Light blue header background
  settingsModalInputBg: '#ffffff',       // Input background
  settingsModalInputBorder: '#5e81b3',   // Input border
  settingsModalInputText: '#406ea7',     // Input text
  settingsModalButtonPrimary: '#406ea7', // Primary button (save)
  settingsModalButtonHover: '#5e81b3',   // Button hover state
  settingsModalButtonText: '#e4e9f2',    // Button text
  settingsModalButtonCancel: '#afbed9',  // Cancel button
  settingsModalAccent: '#406ea7',        // Accent color for headers
  settingsModalSectionBorder: '#cad3e6', // Section dividers

};

const everGreenTheme = {
  name: 'everGreen',

  // Sidebar
// 🎯 LEFT SIDEBAR CORE COLORS - EVERGREEN THEME
// IMPROVED EVERGREEN COLORS:
panelBg: '#1f3b1f',           // Keep deep green background
panelText: '#ffffff',         // WHITE text instead of lime green (much better contrast)
panelBorder: '#4caf50',       // Keep green borders
panelHover: '#2e572e',        // Keep hover color
panelSelected: '#4caf50',     // Brighter green for selection
panelSelectedText: '#ffffff', // White text on selection
panelDanger: '#d32f2f',       // Keep red danger
panelMuted: '#a5d6a7',        // Lighter green for secondary text
panelHeaderBg: '#388e3c',     // Darker green headers for better contrast

countBadgeBg: '#66bb6a',      // More saturated green for note counts
badgeSuccess: '#4caf50',      // Classic green for "New" badges
iconFolder: '#ffb74d',        // Warm orange folder icons (complements green)
iconFile: '#81c784',          // Soft green file icons  
iconCode: '#64b5f6',          // Blue code icons (complements green)
iconImage: '#ba68c8',         // Purple image icons (complements green)

  // MainPanel
  mainPanelBg: '#f1f8e9',      // Very light green (almost white)
  mainPanelText: '#2e7d32',    // Dark green for readability
  mainPanelBorder: '#a5d6a7',  // Muted green borders
  mainPanelInputBg: '#ffffff', // White input background
  mainPanelInputBorder: '#4caf50', // Green border
  mainPanelHeaderBg: '#c8e6c9', // Soft green headers
  mainPanelAccent: '#4caf50',   // Bright green for accents

  // Buttons
  mainPanelButtonPrimary: '#388e3c',  // Rich green
  mainPanelButtonHover: '#2e7d32',    // Darker hover
  mainPanelButtonText: '#ffffff',     // White text
  mainPanelButtonCancel: '#a5d6a7',   // Muted green
  mainPanelButtonDanger: '#d32f2f',   // Red for delete actions

  // Modals
  mainPanelModalBg: '#f1f8e9',       // Very light green
  mainPanelModalOverlay: 'rgba(76, 175, 80, 0.5)',  // Green overlay
  mainPanelModalBorder: '#4caf50',   // Classic green border

  // Sticky Notes
  stickyNoteBg: '#ffffff',          // White sticky notes
  stickyNoteText: '#2e7d32',        // Dark green text
  stickyNoteBorder: '#a5d6a7',      // Muted green border
  stickyNoteDragBar: '#81c784',     // Soft green drag bar
  stickyNoteButtonBg: '#388e3c',    // Rich green button
  stickyNoteButtonText: '#ffffff',  // White text
  stickyNoteButtonHover: '#2e7d32', // Dark green hover
  stickyNoteResizeHandle: '#a5d6a7', // Muted green handle


  // Right Panel
  rightPanelBg: '#f1f8e9',      // Light green
  rightPanelText: '#2e7d32',    // Dark green
  rightPanelBorder: '#4caf50',  // Green accent border
  rightPanelShadow: 'rgba(76, 175, 80, 0.1)', // Soft green shadow

  // Checklist
  stickyNoteChecklistBg: '#ffffff',       // White background
  stickyNoteChecklistBorder: '#a5d6a7',   // Muted green border
  stickyNoteChecklistDragBar: '#81c784',  // Soft green drag bar
  stickyNoteChecklistText: '#2e7d32',     // Dark green text
  stickyNoteChecklistTextChecked: '#4caf50', // Bright green for checked items
  stickyNoteChecklistLine: '#a5d6a7',     // Muted green underline
  stickyNoteChecklistAccent: '#4caf50',   // Bright green accent

  // Checklist Buttons
  stickyNoteChecklistButtonBg: '#388e3c',  // Rich green
  stickyNoteChecklistButtonHover: '#2e7d32', // Darker green hover
  stickyNoteChecklistButtonText: '#ffffff',  // White text

  // List Panel
  stickyListHeaderBg: '#c8e6c9',     // Soft green for list header
  stickyListHeaderText: '#2e7d32',   // Dark green text

  // Code Block (For Theming)
  codeBlockBg: '#c8e6c9',   // Light pastel green
  codeBlockText: '#1b5e20', // Dark green for readability
  codeBlockBorder: '#81c784', // Soft green border
};

const ringmasterTheme = {
  name: 'ringmaster',

  // Sidebar specific colors
  sidebarBg: '#800080',          // Deep regal purple for sidebar background
  sidebarText: '#FFD700',        // Gold text for contrast
  sidebarHoverBg: '#9400D3',     // Darker purple for hover state
  sidebarSelectedBg: '#C0C0C0',  // Silver for selected items
  sidebarContextMenuBg: '#E6E6FA', // Soft lavender for context menu
  sidebarContextMenuBorder: '#DC143C', // Crimson for strong contrast
  sidebarScrollbarBg: '#C0C0C0',     // Silver scrollbar
  sidebarScrollbarHoverBg: '#FFD700', // Gold hover

  // Input and button colors
  inputBg: '#E6E6FA',            // Soft lavender for input background
  inputBorder: '#800080',        // Regal purple for input borders
  inputText: '#00008B',          // Deep blue for readability
  buttonBg: '#800080',           // Deep regal purple for buttons
  buttonHoverBg: '#9400D3',      // Slightly darker purple for hover
  buttonText: '#FFD700',         // Gold text for buttons

  // Accent color
  accentColor: '#DC143C',        // Crimson for emphasis
  accentTextColor: '#ffffff',

  // MainPanel colors
  mainPanelBg: '#E6E6FA',        // Soft lavender for background
  mainPanelText: '#00008B',      // Deep blue text
  mainPanelBorder: '#C0C0C0',    // Silver borders
  mainPanelInputBg: '#FFFFFF',   // White for input backgrounds
  mainPanelInputBorder: '#800080', // Purple input borders
  mainPanelHeaderBg: '#FFD700',  // Gold header for striking contrast
  mainPanelAccent: '#DC143C',    // Crimson accents

  // Button colors
  mainPanelButtonPrimary: '#800080',  // Regal purple
  mainPanelButtonHover: '#9400D3',    // Darker purple hover
  mainPanelButtonText: '#FFD700',     // Gold text
  mainPanelButtonCancel: '#C0C0C0',   // Silver for cancel actions
  mainPanelButtonDanger: '#DC143C',   // Crimson for delete actions

  // Modal colors
  mainPanelModalBg: '#E6E6FA',       // Soft lavender background
  mainPanelModalOverlay: 'rgba(128, 0, 128, 0.5)', // Transparent purple overlay
  mainPanelModalBorder: '#DC143C',   // Crimson for emphasis

  // Sticky Notes
  stickyNoteBg: '#FFFFFF',          // White for contrast
  stickyNoteText: '#00008B',        // Deep blue text
  stickyNoteBorder: '#C0C0C0',      // Silver border
  stickyNoteDragBar: '#FFD700',     // Gold drag bar for pop
  stickyNoteButtonBg: '#800080',    // Regal purple buttons
  stickyNoteButtonText: '#FFD700',  // Gold text
  stickyNoteButtonHover: '#9400D3', // Darker purple hover
  stickyNoteResizeHandle: '#C0C0C0', // Silver handle


  // Right Panel specific
  rightPanelBg: '#E6E6FA',         // Soft lavender
  rightPanelText: '#00008B',       // Deep blue
  rightPanelBorder: '#DC143C',     // Crimson border
  rightPanelShadow: 'rgba(128, 0, 128, 0.1)', // Soft purple shadow

  // Checklist styling
  stickyNoteChecklistBg: '#FFFFFF',        // White background
  stickyNoteChecklistBorder: '#C0C0C0',    // Silver border
  stickyNoteChecklistDragBar: '#FFD700',   // Gold for contrast
  stickyNoteChecklistText: '#00008B',      // Deep blue text
  stickyNoteChecklistTextChecked: '#9400D3', // Lighter purple for checked
  stickyNoteChecklistLine: '#C0C0C0',      // Silver underline
  stickyNoteChecklistAccent: '#DC143C',    // Crimson for emphasis

  // Checklist Buttons
  stickyNoteChecklistButtonBg: '#800080',    // Regal purple
  stickyNoteChecklistButtonHover: '#9400D3', // Darker purple
  stickyNoteChecklistButtonText: '#FFD700',  // Gold text
  
  // List panel
  stickyListHeaderBg: '#FFD700',      // Light blue for list panel header 
  stickyListHeaderText: '#00008B',    // Dark blue for header text
};

const bloomingMeadowsTheme = {
  name: 'bloomingmeadows',

  // Sidebar specific colors
  sidebarBg: '#55B4D4',          // Sky Blue for sidebar background
  sidebarText: '#FFFFFF',        // White for contrast
  sidebarHoverBg: '#88C965',     // Fresh Grass for hover state
  sidebarSelectedBg: '#F18D9E',  // Cherry Blossom for selected items
  sidebarContextMenuBg: '#F4ECC2', // Buttercup Yellow for context menu
  sidebarContextMenuBorder: '#88C965', // Green for natural contrast
  sidebarScrollbarBg: '#F4ECC2',     // Buttercup Yellow scrollbar
  sidebarScrollbarHoverBg: '#F18D9E', // Cherry Blossom on hover

  // Input and button colors
  inputBg: '#FFFFFF',            // White for clarity
  inputBorder: '#55B4D4',        // Sky Blue borders
  inputText: '#2E4A62',          // Darker blue for readability
  buttonBg: '#88C965',           // Fresh Grass for buttons
  buttonHoverBg: '#55B4D4',      // Sky Blue for hover
  buttonText: '#FFFFFF',         // White text for contrast

  // Accent color
  accentColor: '#F18D9E',        // Cherry Blossom Pink for emphasis
  accentTextColor: '#ffffff',

  // MainPanel colors
  mainPanelBg: '#F4ECC2',        // Buttercup Yellow for warmth
  mainPanelText: '#2E4A62',      // Deep blue-gray for readability
  mainPanelBorder: '#88C965',    // Fresh Grass for borders
  mainPanelInputBg: '#FFFFFF',   // White input background
  mainPanelInputBorder: '#55B4D4', // Sky Blue input borders
  mainPanelHeaderBg: '#88C965',  // Fresh Grass for header
  mainPanelAccent: '#F18D9E',    // Cherry Blossom Pink for accents

  // Button colors
  mainPanelButtonPrimary: '#88C965',  // Fresh Grass for primary buttons
  mainPanelButtonHover: '#55B4D4',    // Sky Blue for hover
  mainPanelButtonText: '#FFFFFF',     // White for readability
  mainPanelButtonCancel: '#F4ECC2',   // Buttercup Yellow for soft cancel
  mainPanelButtonDanger: '#F18D9E',   // Cherry Blossom Pink for delete

  // Modal colors
  mainPanelModalBg: '#F4ECC2',       // Buttercup Yellow background
  mainPanelModalOverlay: 'rgba(85, 180, 212, 0.5)', // Sky Blue overlay
  mainPanelModalBorder: '#F18D9E',   // Cherry Blossom border

  // Sticky Notes
  stickyNoteBg: '#FFFFFF',          // White for clean contrast
  stickyNoteText: '#2E4A62',        // Deep blue-gray text
  stickyNoteBorder: '#88C965',      // Fresh Grass border
  stickyNoteDragBar: '#F18D9E',     // Cherry Blossom drag bar
  stickyNoteButtonBg: '#55B4D4',    // Sky Blue buttons
  stickyNoteButtonText: '#FFFFFF',  // White text
  stickyNoteButtonHover: '#88C965', // Fresh Grass hover
  stickyNoteResizeHandle: '#F4ECC2', // Buttercup Yellow handle


  // Right Panel specific
  rightPanelBg: '#F4ECC2',         // Buttercup Yellow
  rightPanelText: '#2E4A62',       // Deep blue-gray
  rightPanelBorder: '#88C965',     // Fresh Grass border
  rightPanelShadow: 'rgba(85, 180, 212, 0.1)', // Soft Sky Blue shadow

  // Checklist styling
  stickyNoteChecklistBg: '#FFFFFF',        // White background
  stickyNoteChecklistBorder: '#88C965',    // Fresh Grass border
  stickyNoteChecklistDragBar: '#F18D9E',   // Cherry Blossom contrast
  stickyNoteChecklistText: '#2E4A62',      // Deep blue-gray text
  stickyNoteChecklistTextChecked: '#55B4D4', // Sky Blue for checked
  stickyNoteChecklistLine: '#88C965',      // Fresh Grass underline
  stickyNoteChecklistAccent: '#F18D9E',    // Cherry Blossom accents

  // Checklist Buttons
  stickyNoteChecklistButtonBg: '#55B4D4',    // Sky Blue
  stickyNoteChecklistButtonHover: '#88C965', // Fresh Grass
  stickyNoteChecklistButtonText: '#FFFFFF',  // White text

    // List panel
    stickyListHeaderBg: '#F18D9E',      // Light blue for list panel header 
    stickyListHeaderText: '#2E4A62',    // Dark blue for header text
  };

    const betterNoteTheme = {
      name: 'betternote',
    
      // 🎨 Sidebar (Notebooks Panel)
      sidebarBg: '#5E328D',         // OneNote Purple
      sidebarText: '#FFFFFF',       // White text for contrast
      sidebarHoverBg: '#7546A0',    // Lighter purple hover
      sidebarSelectedBg: '#A47AC4', // Soft lavender for selected items
      sidebarContextMenuBg: '#E3DFFF', // Light pastel purple
      sidebarContextMenuBorder: '#7546A0', // Medium purple border
      sidebarScrollbarBg: '#A47AC4',  // Light purple scrollbar
      sidebarScrollbarHoverBg: '#7546A0', // Darker purple hover
    
      // ✏️ Input and Buttons
      inputBg: '#FFFFFF',         // White for input backgrounds
      inputBorder: '#5E328D',     // Purple accent border
      inputText: '#2C2C2C',       // Dark gray for readability
      buttonBg: '#5E328D',        // OneNote Purple buttons
      buttonHoverBg: '#7546A0',   // Darker hover effect
      buttonText: '#FFFFFF',      // White text for contrast
    
      // 🌟 Accent Colors
      accentColor: '#A47AC4',     // Soft lavender highlights
      accentTextColor: '#FFFFFF',
    
      // 📄 MainPanel (Note Editing Area)
      mainPanelBg: '#F3F3F3',     // Light gray (neutral)
      mainPanelText: '#2C2C2C',   // Dark gray for high readability
      mainPanelBorder: '#D1D1D1', // Light gray borders
      mainPanelInputBg: '#FFFFFF', // White for input fields
      mainPanelInputBorder: '#5E328D', // Purple border for input
      mainPanelHeaderBg: '#A47AC4', // Soft lavender header
      mainPanelAccent: '#5E328D',   // Purple accents
    
      // 📌 Buttons
      mainPanelButtonPrimary: '#5E328D', // OneNote Purple
      mainPanelButtonHover: '#7546A0',   // Darker hover
      mainPanelButtonText: '#FFFFFF',    // White text
      mainPanelButtonCancel: '#A47AC4',  // Lavender cancel button
      mainPanelButtonDanger: '#D9534F',  // Red for delete actions
    
      // 🔲 Modals
      mainPanelModalBg: '#FFFFFF',       // White background for clarity
      mainPanelModalOverlay: 'rgba(94, 50, 141, 0.5)',  // Semi-transparent purple overlay
      mainPanelModalBorder: '#7546A0',   // Purple border for consistency
    
      // 📌 Sticky Notes (Like OneNote Sections)
      stickyNoteBg: '#FFFFFF',         // Clean white
      stickyNoteText: '#2C2C2C',       // Dark gray text
      stickyNoteBorder: '#A47AC4',     // Lavender border
      stickyNoteDragBar: '#C0B1D9',    // Soft pastel purple for drag
      stickyNoteButtonBg: '#5E328D',   // Purple buttons
      stickyNoteButtonText: '#FFFFFF', // White text
      stickyNoteButtonHover: '#7546A0', // Hover effect
      stickyNoteResizeHandle: '#A47AC4', // Lavender resize handle

    
      // 📜 Right Panel (Tags, Meta Data)
      rightPanelBg: '#F3F3F3',         // Light gray for neutral UI
      rightPanelText: '#2C2C2C',       // Dark gray text
      rightPanelBorder: '#D1D1D1',     // Soft gray border
      rightPanelShadow: 'rgba(94, 50, 141, 0.1)', // Soft purple shadow
    
      // ✅ Checklists
      stickyNoteChecklistBg: '#FFFFFF',       // White background
      stickyNoteChecklistBorder: '#A47AC4',   // Lavender border
      stickyNoteChecklistDragBar: '#C0B1D9',  // Soft purple bar
      stickyNoteChecklistText: '#2C2C2C',     // Dark gray text
      stickyNoteChecklistTextChecked: '#5E328D', // Purple for checked items
      stickyNoteChecklistLine: '#A47AC4',     // Soft lavender underline
      stickyNoteChecklistAccent: '#5E328D',   // Purple accent
    
      // ✔️ Checklist Buttons
      stickyNoteChecklistButtonBg: '#5E328D', // OneNote Purple
      stickyNoteChecklistButtonHover: '#7546A0', // Darker hover
      stickyNoteChecklistButtonText: '#FFFFFF', // White text
    
      // 📚 List Panel (Rainbow Tabs like OneNote)
      stickyListHeaderBg: '#E3DFFF',   // Light pastel purple
      stickyListHeaderText: '#5E328D', // Dark purple text
    
      // 💻 Code Blocks (If Any)
      codeBlockBg: '#F5F5F5',  // Very light gray
      codeBlockText: '#2C2C2C', // Dark gray for readability
      codeBlockBorder: '#A47AC4', // Soft purple border
  
       
};



// Now create the themes object
export const themes = {
  dark: darkTheme,
  light: lightTheme,
  ringmaster: ringmasterTheme,
  bloomingmeadows: bloomingMeadowsTheme,  
  everGreen: everGreenTheme,  
  betternote: betterNoteTheme,
    system: {
    ...darkTheme,
    name: 'system'
  }
};

// Rest of your ThemeProvider code stays the same
export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? JSON.parse(saved) : themes.light;  // Explicitly default to light
  });
  // Update theme handler
  const updateTheme = (themeName) => {
    if (themeName === 'system') {
      // Always use light theme as default, ignoring system preference
      setCurrentTheme({
        ...themes.light,
        name: 'system'
      });
    } else {
      // For explicit theme selections, use the selected theme
      setCurrentTheme(themes[themeName]);
    }
  };

  // Effect to apply theme CSS variables
  useEffect(() => {
    const root = document.documentElement;
    console.log('Applying theme:', currentTheme);
    Object.entries(currentTheme).forEach(([key, value]) => {
      if (key !== 'name') {
        console.log(`Setting --${key} to ${value}`);
        root.style.setProperty(`--${key}`, value);
      }
    });
    
    // Save to localStorage
    localStorage.setItem('theme', JSON.stringify(currentTheme));
  }, [currentTheme]);

  // Effect to handle system theme changes
  useEffect(() => {
    if (currentTheme.name === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        setCurrentTheme({
          ...themes[e.matches ? 'dark' : 'light'],
          name: 'system'
        });
      };

      // Modern event listener
      mediaQuery.addEventListener('change', handleChange);
      // Initial check
      handleChange(mediaQuery);
      
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [currentTheme.name]);

  return (
    <ThemeContext.Provider value={{ theme: currentTheme, updateTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};